import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    ArrowLeft, ChartLine, Users, ChartBar, ClockClockwise, Power,
    DownloadSimple, Circle, ShieldStar, SignIn, SignOut, Warning,
} from "@phosphor-icons/react";
import { SegmentedControl } from "@/components/admin/SegmentedControl";


/* ============================================================================
   AdminCoeReports — `/admin/coe/reports`
   ========================================================================== */


class ReportBoundary extends React.Component {
    /* Wraps a single report's render so a thrown error inside one report does
       not blank-screen the entire page. Friendly visible fallback + console
       trace are good enough at admin scale. */
    constructor(props) {
        super(props);
        this.state = { err: null };
    }
    static getDerivedStateFromError(err) {
        return { err };
    }
    componentDidCatch(err, info) {
        // Surface in dev tools without breaking the UI
        // eslint-disable-next-line no-console
        console.error("[CoeReports] render failed", err, info);
    }
    componentDidUpdate(prev) {
        if (prev.resetKey !== this.props.resetKey && this.state.err) {
            this.setState({ err: null });
        }
    }
    render() {
        if (this.state.err) {
            return (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 flex items-start gap-3" data-testid="coe-reports-render-error">
                    <Warning size={22} weight="fill" className="text-rose-700 shrink-0" />
                    <div className="text-sm">
                        <div className="font-semibold text-rose-900">This report couldn't render.</div>
                        <div className="text-rose-800/80 mt-1">
                            {String(this.state.err?.message || this.state.err) || "Unknown error"}
                        </div>
                        <div className="text-[11px] text-rose-700/70 mt-2">Switch to another report and try again — full details are in your browser console.</div>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const REPORTS = [
    { id: "overview",     label: "Overview",          Icon: ChartLine,     desc: "Headline KPIs across the COE team — total, active, online, pending invites, open workload." },
    { id: "per-type",     label: "Per-type rollup",   Icon: Users,         desc: "Member count, active vs disabled, online now, and workload aggregated by staff_type." },
    { id: "activations",  label: "Activation log",    Icon: Power,         desc: "Activate + deactivate events with the comment that justified the change." },
    { id: "sessions",     label: "Sessions",          Icon: ClockClockwise,desc: "Per-member login frequency and average session duration over the window." },
    { id: "workload",     label: "Workload heatmap",  Icon: ChartBar,      desc: "Open-leads distribution across the active roster — see who's underwater." },
];

const WINDOW_PRESETS = [
    { id: "7d",   label: "Last 7 days",  days: 7 },
    { id: "30d",  label: "Last 30 days", days: 30 },
    { id: "90d",  label: "Last 90 days", days: 90 },
    { id: "365d", label: "Last year",    days: 365 },
];

const fmtDT = (s) =>
    s
        ? new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : "—";

const fmtMins = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const r = Math.round(m - h * 60);
    return r > 0 ? `${h}h ${r}m` : `${h}h`;
};

/* Convert any JSON array of flat objects into a CSV string, suitable for
   the client-side download flow. Quoting wraps values that contain commas
   or quotes; cells with line breaks are flattened to a single space. */
function toCsv(rows) {
    if (!rows?.length) return "";
    const cols = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
    const esc = (v) => {
        if (v == null) return "";
        const s = String(v).replace(/\r?\n/g, " ");
        return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

function downloadCsv(filename, csv) {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}


export default function AdminCoeReports() {
    const [reportId, setReportId] = useState("overview");
    const [windowDays, setWindowDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        setData(null);
        const params = {};
        if (["activations", "sessions"].includes(reportId)) params.days = windowDays;
        api.get(`/admin/coe/reports/${reportId}`, { params })
            .then((r) => setData(r.data))
            .catch((e) => setData({ __error: e.response?.data?.detail || "Failed to load report" }))
            .finally(() => setLoading(false));
    }, [reportId, windowDays]);

    const current = REPORTS.find((r) => r.id === reportId);
    const showWindow = ["activations", "sessions"].includes(reportId);

    return (
        <div className="space-y-5" data-testid="admin-coe-reports">
            <Link to="/admin/coe" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                data-testid="coe-reports-back">
                <ArrowLeft size={14} /> Back to COE Command Center
            </Link>

            {/* Hero */}
            <div className="rounded-2xl border border-border bg-card p-5 sm:p-6">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin · COE</div>
                <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5">Reports</h1>
                <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
                    Operational intelligence for the COE team — headline KPIs, per-type rollups, activation accountability, session frequency, and workload distribution.
                </p>
            </div>

            {/* Report selector */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2.5" data-testid="coe-reports-selector">
                {REPORTS.map((r) => {
                    const on = reportId === r.id;
                    return (
                        <button
                            key={r.id}
                            type="button"
                            onClick={() => setReportId(r.id)}
                            className={`text-left rounded-2xl p-4 border transition ${on ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/40 hover:-translate-y-0.5"}`}
                            data-testid={`coe-report-pick-${r.id}`}
                        >
                            <div className={`w-9 h-9 rounded-xl ${on ? "bg-primary text-white" : "bg-primary/10 text-primary"} grid place-items-center mb-2.5`}>
                                <r.Icon size={16} weight="duotone" />
                            </div>
                            <div className="font-semibold text-sm">{r.label}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">{r.desc}</div>
                        </button>
                    );
                })}
            </div>

            {/* Controls strip */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-border bg-card p-4">
                <div className="min-w-0">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Active report</div>
                    <div className="font-display text-xl font-semibold">{current?.label}</div>
                </div>
                {showWindow && (
                    <SegmentedControl
                        options={WINDOW_PRESETS.map((p) => ({ value: p.days, label: p.label }))}
                        value={windowDays}
                        onChange={setWindowDays}
                        testid="coe-reports-window"
                    />
                )}
            </div>

            {/* Body — wrapped in a boundary so a runtime error in one report
                never blanks the whole page. resetKey clears the error when
                the user switches report or window. */}
            <ReportBoundary resetKey={`${reportId}:${windowDays}`}>
                {loading ? (
                    <div className="text-center text-muted-foreground py-16">Loading report…</div>
                ) : data?.__error ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-rose-900" data-testid="coe-reports-error">
                        {data.__error}
                    </div>
                ) : reportId === "overview" ? (
                    <OverviewReport data={data} />
                ) : reportId === "per-type" ? (
                    <PerTypeReport rows={data} />
                ) : reportId === "activations" ? (
                    <ActivationsReport data={data} windowDays={windowDays} />
                ) : reportId === "sessions" ? (
                    <SessionsReport data={data} windowDays={windowDays} />
                ) : reportId === "workload" ? (
                    <WorkloadReport data={data} />
                ) : null}
            </ReportBoundary>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */
function OverviewReport({ data }) {
    if (!data) return null;
    const tiles = [
        { key: "total_members", label: "Total members", value: data.total_members, tone: "primary", Icon: Users },
        { key: "active_members", label: "Active", value: data.active_members, tone: "emerald", Icon: Users },
        { key: "disabled_members", label: "Disabled", value: data.disabled_members, tone: "rose", Icon: Users },
        { key: "online_now", label: "Online now", value: data.online_now, tone: "emerald", Icon: Circle, sub: "Logged in within 24h" },
        { key: "invites_pending", label: "Pending invites", value: data.invites_pending, tone: "amber", Icon: ShieldStar },
        { key: "open_total", label: "Open workload", value: data.open_total, tone: "violet", Icon: ChartBar, sub: `web ${data.open_web_leads} · b2b ${data.open_b2b_leads} · apps ${data.open_applications}` },
    ];
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="coe-report-overview">
            {tiles.map(({ key, ...rest }) => <Kpi key={key} {...rest} />)}
        </div>
    );
}

const TONE_KPI = {
    primary: { bg: "bg-primary/5", border: "border-primary/30", icon: "text-primary" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-700" },
    amber:   { bg: "bg-amber-50",   border: "border-amber-200",   icon: "text-amber-700" },
    rose:    { bg: "bg-rose-50",    border: "border-rose-200",    icon: "text-rose-700" },
    violet:  { bg: "bg-violet-50",  border: "border-violet-200",  icon: "text-violet-700" },
};

function Kpi({ label, value, tone, Icon, sub }) {
    const t = TONE_KPI[tone] || TONE_KPI.primary;
    return (
        <div className={`rounded-2xl border ${t.border} ${t.bg} p-5`} data-testid={`coe-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2">
                <Icon size={14} weight="fill" className={t.icon} />
                <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-muted-foreground">{label}</div>
            </div>
            <div className="font-display text-3xl font-bold tabular-nums mt-2">{value ?? "—"}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */
function PerTypeReport({ rows }) {
    /* Tolerant of unexpected shapes: if the backend ever returns {error: ...}
       or wraps the array in an envelope, we fall back gracefully instead of
       throwing inside Math.max / spread / .map. */
    const safeRows = Array.isArray(rows) ? rows : Array.isArray(rows?.rows) ? rows.rows : [];
    if (!safeRows.length) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground text-sm" data-testid="coe-per-type-empty">
                No staff types to roll up yet.
            </div>
        );
    }
    const maxLoad = safeRows.reduce((m, r) => Math.max(m, Number(r?.open_leads) || 0), 1);
    const ordered = [...safeRows].sort((a, b) => (Number(b?.active) || 0) - (Number(a?.active) || 0));
    return (
        <div className="space-y-3" data-testid="coe-report-per-type">
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    onClick={() => downloadCsv("coe_per_type.csv", toCsv(safeRows))}
                    className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
                    data-testid="coe-per-type-csv"
                >
                    <DownloadSimple size={12} weight="bold" /> Download CSV
                </button>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-3">Role</div>
                    <div className="col-span-2 text-right">Active / Total</div>
                    <div className="col-span-1 text-right">Online</div>
                    <div className="col-span-6">Open workload</div>
                </div>
                {ordered.map((r) => {
                    const open = Number(r?.open_leads) || 0;
                    const pct = Math.round((open / maxLoad) * 100);
                    return (
                        <div key={r?.key || r?.label} className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center" data-testid={`coe-per-type-row-${r?.key}`}>
                            <div className="col-span-3 font-semibold text-sm">{r?.label || r?.key || "—"}</div>
                            <div className="col-span-2 text-right tabular-nums text-sm">
                                <span className="font-bold">{r?.active ?? 0}</span>
                                <span className="text-muted-foreground"> / {r?.total ?? 0}</span>
                                {(r?.disabled || 0) > 0 && <span className="ml-2 text-[10px] uppercase tracking-wide text-rose-700">{r.disabled} off</span>}
                            </div>
                            <div className="col-span-1 text-right tabular-nums">
                                <span className={`inline-flex items-center gap-1 text-xs font-bold ${(r?.online_now || 0) > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
                                    <Circle size={6} weight="fill" /> {r?.online_now ?? 0}
                                </span>
                            </div>
                            <div className="col-span-6">
                                <div className="flex items-center gap-3">
                                    <div className="h-2 flex-1 rounded-full bg-secondary overflow-hidden">
                                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs tabular-nums font-semibold w-10 text-right">{open}</span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */
function ActivationsReport({ data, windowDays }) {
    if (!data) return null;
    return (
        <div className="space-y-3" data-testid="coe-report-activations">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Kpi label={`Deactivations (${windowDays}d)`} value={data.deactivated_count} tone="rose" Icon={Power} />
                <Kpi label={`Activations (${windowDays}d)`} value={data.activated_count} tone="emerald" Icon={Power} />
            </div>
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    onClick={() => downloadCsv("coe_activations.csv", toCsv((data.rows || []).map((r) => ({
                        action: (r.meta?.is_active ? "activated" : "deactivated"),
                        target: r.target_name,
                        staff_type: r.target_staff_type,
                        actor: r.actor_name,
                        when: r.created_at,
                        comment: r.meta?.comment || "",
                    }))))}
                    className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
                    data-testid="coe-activations-csv"
                >
                    <DownloadSimple size={12} weight="bold" /> Download CSV
                </button>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {(!data.rows || data.rows.length === 0) ? (
                    <div className="text-center text-muted-foreground text-sm py-10" data-testid="coe-activations-empty">
                        No activate/deactivate events in this window.
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                            <div className="col-span-1">Action</div>
                            <div className="col-span-3">Target</div>
                            <div className="col-span-2">Actor</div>
                            <div className="col-span-2">When</div>
                            <div className="col-span-4">Reason</div>
                        </div>
                        {data.rows.map((r) => {
                            const activated = r.meta?.is_active === true;
                            return (
                                <Link
                                    key={r.id}
                                    to={`/admin/coe/people/${r.entity_id}`}
                                    className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition text-sm"
                                    data-testid={`coe-activations-row-${r.id}`}
                                >
                                    <div className="col-span-1">
                                        <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${activated ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                            {activated ? "Activated" : "Deactivated"}
                                        </span>
                                    </div>
                                    <div className="col-span-3 min-w-0">
                                        <div className="font-semibold truncate">{r.target_name || r.summary || "—"}</div>
                                        {r.target_staff_type && <div className="text-[11px] text-muted-foreground capitalize">{r.target_staff_type.replace(/_/g, " ")}</div>}
                                    </div>
                                    <div className="col-span-2 text-xs text-muted-foreground truncate">{r.actor_name || "System"}</div>
                                    <div className="col-span-2 text-xs text-muted-foreground tabular-nums">{fmtDT(r.created_at)}</div>
                                    <div className="col-span-4 text-xs text-muted-foreground truncate">{r.meta?.comment || <span className="italic">No reason recorded</span>}</div>
                                </Link>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */
function SessionsReport({ data, windowDays }) {
    if (!data) return null;
    return (
        <div className="space-y-3" data-testid="coe-report-sessions">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Kpi label={`Total logins (${windowDays}d)`} value={data.total_logins} tone="primary" Icon={SignIn} />
                <Kpi label="Unique users" value={data.unique_users} tone="emerald" Icon={Users} />
            </div>
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    onClick={() => downloadCsv("coe_sessions.csv", toCsv(data.rows || []))}
                    className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
                    data-testid="coe-sessions-csv"
                >
                    <DownloadSimple size={12} weight="bold" /> Download CSV
                </button>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {!data.rows?.length ? (
                    <div className="text-center text-muted-foreground text-sm py-10" data-testid="coe-sessions-empty">
                        No login events in this window.
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                            <div className="col-span-4">User</div>
                            <div className="col-span-2">Type</div>
                            <div className="col-span-2 text-right">Logins</div>
                            <div className="col-span-2 text-right">Avg duration</div>
                            <div className="col-span-2">Last login</div>
                        </div>
                        {data.rows.map((r) => (
                            <Link
                                key={r.user_id}
                                to={`/admin/coe/people/${r.user_id}`}
                                className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition text-sm"
                                data-testid={`coe-sessions-row-${r.user_id}`}
                            >
                                <div className="col-span-4 font-semibold truncate">{r.user_name || "—"}</div>
                                <div className="col-span-2 text-xs text-muted-foreground capitalize truncate">{(r.staff_type || "—").replace(/_/g, " ")}</div>
                                <div className="col-span-2 text-right font-bold tabular-nums">{r.login_count}</div>
                                <div className="col-span-2 text-right tabular-nums text-xs">{fmtMins(r.avg_session_minutes)}</div>
                                <div className="col-span-2 text-xs text-muted-foreground tabular-nums">{fmtDT(r.last_login_at)}</div>
                            </Link>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}


/* ──────────────────────────────────────────────────────────────────────── */
function WorkloadReport({ data }) {
    if (!data) return null;
    const max = data.max_load || 1;
    return (
        <div className="space-y-3" data-testid="coe-report-workload">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Kpi label="Max load (one person)" value={data.max_load} tone="rose" Icon={ChartBar} />
                <Kpi label="Median load" value={data.median_load} tone="amber" Icon={ChartBar} />
            </div>
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    onClick={() => downloadCsv("coe_workload.csv", toCsv(data.rows || []))}
                    className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline"
                    data-testid="coe-workload-csv"
                >
                    <DownloadSimple size={12} weight="bold" /> Download CSV
                </button>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                {!data.rows?.length ? (
                    <div className="text-center text-muted-foreground text-sm py-10" data-testid="coe-workload-empty">
                        No active members to chart.
                    </div>
                ) : (
                    data.rows.map((r) => {
                        const pct = Math.round((r.open_total / max) * 100);
                        const w = (r.open_web / max) * 100;
                        const b = (r.open_b2b / max) * 100;
                        const a = (r.open_application / max) * 100;
                        return (
                            <Link
                                key={r.id}
                                to={`/admin/coe/people/${r.id}`}
                                className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center hover:bg-secondary/30 transition"
                                data-testid={`coe-workload-row-${r.id}`}
                            >
                                <div className="col-span-3 flex items-center gap-2.5 min-w-0">
                                    {r.image ? (
                                        <img src={r.image} alt={r.name} className="w-7 h-7 rounded-full object-cover ring-1 ring-border" />
                                    ) : (
                                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[10px] grid place-items-center font-semibold shrink-0">
                                            {(r.name || "?").slice(0, 2).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-semibold text-sm truncate">{r.name || "—"}</div>
                                        <div className="text-[10px] text-muted-foreground capitalize">{(r.staff_type || "").replace(/_/g, " ")}</div>
                                    </div>
                                </div>
                                <div className="col-span-8">
                                    <div className="h-3 rounded-full bg-secondary overflow-hidden flex" aria-label={`${r.open_total} open`}>
                                        <div className="h-full bg-sky-500" style={{ width: `${w}%` }} />
                                        <div className="h-full bg-violet-500" style={{ width: `${b}%` }} />
                                        <div className="h-full bg-amber-500" style={{ width: `${a}%` }} />
                                    </div>
                                    <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground tabular-nums">
                                        {r.open_web > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 mr-1" />{r.open_web} web</span>}
                                        {r.open_b2b > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 mr-1" />{r.open_b2b} b2b</span>}
                                        {r.open_application > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 mr-1" />{r.open_application} apps</span>}
                                    </div>
                                </div>
                                <div className="col-span-1 text-right tabular-nums font-bold">{r.open_total}</div>
                            </Link>
                        );
                    })
                )}
            </div>
        </div>
    );
}
